import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { MaskLines } from "@/components/Primitives";
import { EASE } from "@/lib/motion";
import { scrollToTarget } from "@/lib/scroll";

const HEADLINE = [
  "Building Digital",
  "Experiences That",
  <>
    <em className="font-light italic text-ivory">Matter</em>
    <span className="text-gold">.</span>
  </>,
];


const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: EASE },
});

export const Hero = () => {
  const ref = useRef(null);
  const [desktop, setDesktop] = useState(false);
  useEffect(() => {
    setDesktop(window.matchMedia("(min-width: 1024px)").matches);
  }, []);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const spring = { stiffness: 60, damping: 20 };
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), spring);
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), spring);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const artY = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={ref}
      onMouseMove={onMove}
      className="relative flex min-h-[100svh] flex-col overflow-hidden pt-28"
      data-testid="hero-section"
    >

      <div className="container-x relative z-10 grid flex-1 grid-cols-1 items-center gap-12 pb-24 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: desktop ? textY : 0 }} className="lg:col-span-7">
          <motion.div {...fade(0.15)} className="flex items-center gap-4">
            <motion.span
              className="h-px w-12 origin-left bg-gold"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.2, delay: 0.2, ease: EASE }}
            />
            <span className="eyebrow" data-testid="hero-label">Independent Developer &amp; Creator</span>
          </motion.div>
          <MaskLines
            as="h1"
            onLoad
            delay={0.35}
            lines={HEADLINE}
            testId="hero-headline"
            className="mt-8 font-serif text-[2.9rem] font-light leading-[0.98] tracking-[-0.02em] text-ivory sm:text-7xl lg:text-[5.4rem] xl:text-[6.4rem]"
          />
          <motion.p {...fade(0.95)} className="mt-10 max-w-xl text-base leading-relaxed text-ash sm:text-lg" data-testid="hero-description">
            I design and develop websites, software, and digital experiences with a focus on clean code, thoughtful design, and meaningful functionality.
          </motion.p>
          <motion.div {...fade(1.1)} className="mt-12 flex flex-col gap-4 sm:flex-row">
            <button className="btn btn-primary justify-center" onClick={() => scrollToTarget("#work")} data-testid="hero-explore-btn">
              Explore My Work <ArrowRight size={16} />
            </button>
            <button className="btn btn-ghost justify-center" onClick={() => scrollToTarget("#contact")} data-testid="hero-contact-btn">
              Get In Touch <ArrowDownRight size={16} />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: desktop ? artY : 0 }}
          className="relative w-full lg:col-span-4 lg:col-start-9"
          data-testid="hero-visual"
        >
          <div style={{ perspective: "1200px" }}>
            <motion.div
              style={{ rotateX: rx, rotateY: ry }}
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.6, delay: 0.5, ease: EASE }}
              className="relative aspect-[4/3] overflow-hidden bg-coal lg:aspect-[3/4]"
            >
              <motion.img
                src="/images/tunnel.jpg"
                alt="Light falling through a concrete corridor"
                initial={{ scale: 1.2 }}
                animate={{ scale: 1.05 }}
                transition={{ duration: 2.4, delay: 0.5, ease: EASE }}
                className="h-full w-full object-cover grayscale"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <motion.button
        {...fade(1.4)}
        onClick={() => scrollToTarget("#about")}
        className="container-x relative z-10 mb-8 flex items-center gap-4 font-mono text-xs uppercase tracking-[0.25em] text-ash hover:text-ivory"
        data-testid="hero-scroll-indicator"
        aria-label="Scroll to About"
      >
        <span className="relative block h-12 w-px overflow-hidden bg-line">
          <span className="scroll-dot absolute left-0 top-0 block h-4 w-px bg-gold" />
        </span>
        Scroll
      </motion.button>
    </section>
  );
};
